import type { SelectedView } from "../periods/usePeriods";

// Present mode opens in a new browser tab (so presenters can screen-share it
// without exposing the rest of Vitals), which means the client page's
// selected view can't travel via React Router navigation state — a new tab
// has no history entry to carry it. Encoding it into the URL instead means it
// also survives a reload of the Present tab itself.
//
// The client detail page (see ClientDetailPage.tsx) reuses this same codec
// for its own URL-synced view *and* its comparison view, which need distinct
// query keys to coexist — hence the optional `prefix` (e.g. "compare" turns
// "mode" into "compareMode"). Prefix defaults to "" so existing callers that
// build a standalone URLSearchParams (the Present link) are unaffected.
function paramKey(prefix: string, suffix: string): string {
  if (!prefix) return suffix;
  return `${prefix}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
}

const VIEW_PARAM_SUFFIXES = ["mode", "periodId", "from", "to"] as const;

export function encodeViewParams(view: SelectedView, prefix = ""): URLSearchParams {
  const params = new URLSearchParams();
  if (view.mode === "period") {
    params.set(paramKey(prefix, "mode"), "period");
    params.set(paramKey(prefix, "periodId"), view.periodId);
  } else if (view.mode === "range") {
    params.set(paramKey(prefix, "mode"), "range");
    params.set(paramKey(prefix, "from"), view.from);
    params.set(paramKey(prefix, "to"), view.to);
  } else {
    params.set(paramKey(prefix, "mode"), "alltime");
  }
  return params;
}

export function decodeViewParams(params: URLSearchParams, prefix = ""): SelectedView | null {
  const mode = params.get(paramKey(prefix, "mode"));
  if (mode === "period") {
    const periodId = params.get(paramKey(prefix, "periodId"));
    return periodId ? { mode: "period", periodId } : null;
  }
  if (mode === "range") {
    const from = params.get(paramKey(prefix, "from"));
    const to = params.get(paramKey(prefix, "to"));
    return from && to ? { mode: "range", from, to } : null;
  }
  if (mode === "alltime") return { mode: "alltime" };
  return null;
}

// Merges a view into an existing URLSearchParams (in place), clearing any
// stale keys from a previous mode first — e.g. switching period -> range
// must drop the old periodId, not leave it alongside the new from/to.
export function applyViewParams(
  params: URLSearchParams,
  view: SelectedView | null,
  prefix = "",
): void {
  for (const suffix of VIEW_PARAM_SUFFIXES) params.delete(paramKey(prefix, suffix));
  if (!view) return;
  for (const [key, value] of encodeViewParams(view, prefix)) params.set(key, value);
}
